import type { BundleRowStatus, BundleUploadRow } from "../store/useBundleUploads";

// Pipeline order: work in flight, then what needs the user, then the rest.
const STAGES: Array<[BundleRowStatus, (count: number) => string]> = [
  ["uploading", () => "uploading"],
  ["preparing", () => "preparing"],
  ["splitting", () => "splitting"],
  ["saving", () => "saving"],
  ["needs-review", (count) => (count === 1 ? "needs review" : "need review")],
  ["failed", () => "failed"],
  ["ready", () => "not started"],
  ["done", () => "done"],
];

/**
 * How many uploads are at each stage, e.g. "1 uploading · 2 splitting" — for
 * the whole pop-up or one case's section of it (MDRS-550). Empty for none.
 */
export const stageSummary = (rows: Pick<BundleUploadRow, "status">[]): string => {
  const counts = new Map<BundleRowStatus, number>();
  rows.forEach(({ status }) => counts.set(status, (counts.get(status) ?? 0) + 1));

  return STAGES.flatMap(([status, label]) => {
    const count = counts.get(status) ?? 0;
    return count > 0 ? [`${count} ${label(count)}`] : [];
  }).join(" · ");
};

/**
 * Bundle toast header: the picker on its own, or the stage counts — the only
 * part still on screen while the pop-up is minimised (MDRS-550), e.g.
 * "Bundle uploads · 1 uploading · 2 splitting".
 */
export const bundleToastTitle = (rows: Pick<BundleUploadRow, "status">[]): string =>
  rows.length === 0
    ? "Uploading Bundle File"
    : `Bundle uploads · ${stageSummary(rows)}`;
